import React, { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import { Alert, DatePicker, Divider, Select, Space, Spin, Tag } from 'antd'
import dayjs from 'dayjs'

import Layout from '../App/layout'
import LayoutContentWrapper from '../../components/utility/layoutWrapper'
import ContentLayout from '../../components/utility/layoutContent'
import { HIGH_VALUE_BASKET_SUMMARY_QUERY } from './graphql'
import { formatBaht } from './format'
import SummaryCards from './SummaryCards'
import AssigneeTable, { assigneeKeyOf } from './AssigneeTable'
import LeadsTable, { STATUSES } from './LeadsTable'
import OnlinePaymentsDrawer from './OnlinePaymentsDrawer'

const MONTH_FORMAT = 'YYYY-MM'
const DATE_FORMAT = 'YYYY-MM-DD'
const DEFAULT_MIN_GRAND_TOTAL = 90000
const MIN_GRAND_TOTAL_OPTIONS = [50000, 90000, 200000, 500000, 1000000]
const DEFAULT_STATUS = 'open'

const parseMonth = (value) => {
  const month = value ? dayjs(value, MONTH_FORMAT, true) : null
  return (month && month.isValid() ? month : dayjs()).startOf('month')
}

const parseMinGrandTotal = (value) => {
  const amount = Number(value)
  return MIN_GRAND_TOTAL_OPTIONS.includes(amount) ? amount : DEFAULT_MIN_GRAND_TOTAL
}

const parseStatus = value => (STATUSES.includes(value) ? value : DEFAULT_STATUS)

const HighValueBasketsIndexPage = () => {
  const { i18n } = useTranslation('highValueBaskets')
  const t = (key, options) => i18n.t(`highValueBaskets:${key}`, options)
  const history = useHistory()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const month = parseMonth(params.get('month'))
  const monthString = month.format(DATE_FORMAT)
  const minGrandTotal = parseMinGrandTotal(params.get('min'))
  const status = parseStatus(params.get('status'))
  const assigneeId = params.get('staffId')
  const isOnlineOpen = params.get('online') === '1'
  const isCurrentMonth = month.isSame(dayjs(), 'month')

  useEffect(() => {
    document.title = `${i18n.t('highValueBaskets:title')} | OSH CRM`
  }, [i18n.language])

  const { loading, data, error } = useQuery(HIGH_VALUE_BASKET_SUMMARY_QUERY, {
    variables: { month: monthString, minGrandTotal },
    fetchPolicy: 'cache-and-network'
  })

  const summary = (data && data.highValueBasketSummary) || null
  const rows = (summary && summary.assignees) || []
  const selectedRow = assigneeId && rows.find(row => assigneeKeyOf(row) === assigneeId)
  const countsRow = selectedRow || (summary && summary.totals) || null

  const updateParams = (changes) => {
    const next = new URLSearchParams(location.search)
    Object.entries(changes).forEach(([key, value]) => {
      if (value) {
        next.set(key, value)
      } else {
        next.delete(key)
      }
    })
    const search = next.toString()
    history.push({ search: search ? `?${search}` : '' })
  }

  const onChangeMonth = (value) => {
    updateParams({ month: value ? value.format(MONTH_FORMAT) : null, staffId: null })
  }

  const onChangeMinGrandTotal = (value) => {
    updateParams({ min: value === DEFAULT_MIN_GRAND_TOTAL ? null : String(value) })
  }

  const onSelectStatus = (value) => {
    updateParams({ status: value === DEFAULT_STATUS ? null : value })
  }

  const onSelectAssignee = (value) => {
    updateParams({ staffId: value })
  }

  const selectedAssigneeName = selectedRow
    ? (selectedRow.assignee ? selectedRow.assignee.fullName : t('assignees.unassigned'))
    : null

  return (
    <Layout>
      <LayoutContentWrapper breadcrumbs={[
        { path: '/high_value_baskets', name: t('title') }
      ]}>
        <ContentLayout>
          <Space wrap>
            <span>{t('filters.month')}</span>
            <DatePicker allowClear={false} format={MONTH_FORMAT} onChange={onChangeMonth} picker="month" value={month} />
            <span>{t('filters.minGrandTotal')}</span>
            <Select
              onChange={onChangeMinGrandTotal}
              options={MIN_GRAND_TOTAL_OPTIONS.map(value => ({ value, label: formatBaht({ value }) }))}
              style={{ minWidth: 140 }}
              value={minGrandTotal}
            />
          </Space>
          <Divider />
          {error && <Alert message={error.message} showIcon style={{ marginBottom: 16 }} type="error" />}
          {!summary && loading && <Spin />}
          {summary && (
            <SummaryCards
              isCurrentMonth={isCurrentMonth}
              onOpenOnlinePayments={() => updateParams({ online: '1' })}
              onSelectStatus={onSelectStatus}
              summary={summary}
            />
          )}

          <h3 style={{ marginTop: 24 }}>{t('assignees.heading')}</h3>
          <p style={{ color: 'rgba(0, 0, 0, 0.45)' }}>{t('assignees.hint')}</p>
          <AssigneeTable
            loading={loading}
            onSelectAssignee={onSelectAssignee}
            rows={rows}
            selectedAssigneeId={assigneeId}
          />

          <h3 style={{ marginTop: 24 }}>
            {t('leads.heading')}
            {selectedAssigneeName && (
              <Tag closable onClose={() => onSelectAssignee(null)} style={{ marginLeft: 8 }}>
                {selectedAssigneeName}
              </Tag>
            )}
          </h3>
          <LeadsTable
            assigneeId={assigneeId}
            countsRow={countsRow}
            minGrandTotal={minGrandTotal}
            month={monthString}
            onSelectStatus={onSelectStatus}
            status={status}
          />

          <OnlinePaymentsDrawer
            month={monthString}
            onClose={() => updateParams({ online: null })}
            open={isOnlineOpen}
          />
        </ContentLayout>
      </LayoutContentWrapper>
    </Layout>
  )
}

export default HighValueBasketsIndexPage
